import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useState } from "react";
import {
  AccountSettingsDropdown,
  IItemProps,
} from "@pages/account-settings/account-settings-dropdown";
import { useSettingsApiClient } from "@api/use-settings-api-client";
import { SettingsApiTypes } from "@stellar/settings-api-client";

type UnitType = SettingsApiTypes.UnitMeasurement;

/** All the unit types mapped to their description */
const units: Record<UnitType, IItemProps<UnitType>> = {
  metric: {
    value: "metric",
    label: "Metric",
    description: "Millimeter (mm) / Centimeter (cm) / Meter (m)",
  },
  imperial: {
    value: "imperial",
    label: "Imperial",
    description: "Inch (in) / Feet (ft) / Yard (yd)",
  },
};

/** A simple unit dropdown. When opened the user can chose between the available units */
export function AccountUnitSelector(): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const [selectedUnit, setSelectedUnit] = useState<IItemProps<UnitType>>(
    units.imperial
  );
  const settingsApiClient = useSettingsApiClient();
  const [updatedModifiedAt, setUpdatedModifiedAt] = useState<string>("0");

  useEffect(() => {
    async function fetchUserSettings(): Promise<void> {
      const { value, modifiedAt } =
        await settingsApiClient.user.fetchUserSettings("unit_measurement");
      setSelectedUnit(units[value]);
      setUpdatedModifiedAt(modifiedAt);
    }
    void fetchUserSettings();
  }, [settingsApiClient]);

  async function onUnitChange(newUnit: UnitType): Promise<void> {
    trackEvent({
      name: UserProfile.changeUnit,
      props: { unit: newUnit },
    });
    const { value, modifiedAt } =
      await settingsApiClient.user.updateUserSettings("unit_measurement", {
        value: newUnit,
        modifiedAt: updatedModifiedAt,
      });
    setSelectedUnit(units[value]);
    setUpdatedModifiedAt(modifiedAt);
  }

  return (
    <AccountSettingsDropdown
      shouldShowDescriptionInTitle={true}
      items={Object.values(units)}
      selectedItem={{
        label: selectedUnit.label,
        value: selectedUnit.value,
        description: selectedUnit.description,
      }}
      onClickHandler={onUnitChange}
    />
  );
}
